import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import { FormGroup, Button, Intent, TextArea } from "@blueprintjs/core";
import { observable } from 'mobx';
import Toast from 'helpers/toaster';
import FormDrawer from 'components/FormDrawer';
import { FormSection, Choice, FieldError } from 'components/FormBits';

const isBlank = (value) => value === undefined || value === null || String(value).trim() === '';

export const PART_TYPES = [
  { value: 'filter', label: 'Filter' },
  { value: 'belt', label: 'Belt' },
  { value: 'others', label: 'Other' },
];

// Bring a field with an error into view and put the cursor in it.
const focusField = (id) => {
  setTimeout(() => {
    const element = document.getElementById(id);
    if (!element) return;
    const field = element.matches('input, select, textarea, button') ? element : element.querySelector('button, input');
    element.scrollIntoView({ block: 'center', behavior: 'smooth' });
    if (field) field.focus({ preventScroll: true });
  }, 0);
};

// Add a part to an equipment (no `part`) or edit one (`part`), in a side panel
// on the equipment page. Works on a copy: Cancel leaves the part as it was.
@inject('equipmentStore') @observer
class AddEquipmentPartModal extends Component {
  @observable draft = {};
  @observable errors = {};

  componentDidMount() {
    if (this.props.isOpen) this.reset();
  }

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) this.reset();
  }

  reset() {
    const part = this.props.part || {};
    this.draft = {
      id: part.id || null,
      name: part.name || '',
      eq_type: part.eq_type || '',
      eq_size: part.eq_size || '',
      description: part.description || '',
    };
    this.errors = {};
  }

  setField(key, value) {
    this.draft[key] = value;
    if (this.errors[key]) this.errors = { ...this.errors, [key]: null };
  }

  save = () => {
    const { equipment, equipmentStore, onClose } = this.props;
    if (equipmentStore.isSavingEquipment) return;
    const draft = this.draft;

    // Inline errors under each missing field, in the order they appear.
    const errors = {};
    if (isBlank(draft.name)) errors.name = 'Enter the part’s name.';
    if (isBlank(draft.eq_type)) errors.eq_type = 'Choose the type of part.';
    if (isBlank(draft.description)) errors.description = 'Add a short description.';
    this.errors = errors;
    const first = [['name', 'equipment-part-name'], ['eq_type', 'equipment-part-type'], ['description', 'equipment-part-description']]
      .find(([key]) => errors[key]);
    if (first) {
      focusField(first[1]);
      return;
    }

    const editing = Boolean(draft.id);
    const part = { ...draft, name: draft.name.trim(), eq_size: draft.eq_size.trim(), description: draft.description.trim() };
    equipmentStore.savePart(equipment, part)
      .then(() => {
        Toast.show({ message: editing ? `${part.name} saved.` : `${part.name} added.`, intent: Intent.SUCCESS });
        onClose();
      })
      .catch((error) => {
        const data = error && error.response && error.response.data;
        const reason = data && (data.errors || data.error);
        Toast.show({
          message: reason ? (Array.isArray(reason) ? reason.join(', ') : reason) : "Couldn't save the part. Try again.",
          intent: Intent.DANGER,
        });
      });
  };

  render() {
    const { isOpen, onClose, equipmentStore } = this.props;
    const editing = Boolean(this.draft.id);
    const busy = Boolean(equipmentStore.isSavingEquipment);
    const { draft, errors } = this;

    return (
      <FormDrawer
        title={editing ? 'Edit equipment part' : 'Add equipment part'}
        icon="wrench"
        isOpen={isOpen}
        onClose={onClose}
        busy={busy}
        footer={
          <>
            <Button text="Cancel" onClick={onClose} disabled={busy} />
            <Button
              intent={Intent.PRIMARY}
              icon={editing ? undefined : 'add'}
              text={editing ? 'Save changes' : 'Add part'}
              loading={busy}
              onClick={this.save}
            />
          </>
        }
      >
        <FormSection title="The part" hint="What it is, so the right one goes on the truck.">
          <FormGroup
            className={errors.name ? 'has-error' : undefined}
            label="Name"
            labelFor="equipment-part-name"
            labelInfo="*"
          >
            <input
              id="equipment-part-name"
              type='text'
              className='bp3-input bp3-fill'
              placeholder='e.g. Blower belt'
              value={draft.name || ''}
              aria-invalid={!!errors.name}
              onChange={(event) => this.setField('name', event.target.value)}
            />
            <FieldError>{errors.name}</FieldError>
          </FormGroup>

          <div id="equipment-part-type">
            <Choice
              label="Type"
              required={true}
              value={draft.eq_type || ''}
              options={PART_TYPES}
              error={errors.eq_type}
              onChange={(value) => this.setField('eq_type', value)}
            />
          </div>

          <FormGroup label="Size" labelFor="equipment-part-size">
            <input
              id="equipment-part-size"
              type='text'
              className='bp3-input bp3-fill'
              placeholder='e.g. 16x25x1 or A42'
              value={draft.eq_size || ''}
              onChange={(event) => this.setField('eq_size', event.target.value)}
            />
          </FormGroup>

          <FormGroup
            className={errors.description ? 'has-error' : undefined}
            label="Description"
            labelFor="equipment-part-description"
            labelInfo="*"
          >
            <TextArea
              id="equipment-part-description"
              className='bp3-input bp3-fill'
              placeholder='e.g. Replace every 6 months; pleated MERV 8'
              value={draft.description || ''}
              aria-invalid={!!errors.description}
              onChange={(event) => this.setField('description', event.target.value)}
            />
            <FieldError>{errors.description}</FieldError>
          </FormGroup>
        </FormSection>
      </FormDrawer>
    );
  }
};

export default AddEquipmentPartModal;
